import React from 'react';
import PropTypes from 'prop-types';
import { cx } from '../../ui/classNames';
import { categoryDisplay } from './categoryTools';
import { getCategoryPillHue } from './categoryPalette';

// Short enough to sit in a 38px grid row with room for the cell focus ring.
// The hue's light and dark tones arrive as CSS variables (see CategoryPill);
// high-contrast themes keep the text colour and draw it as an outline.
const PILL_CLASS_NAME = cx(
    'box-border inline-block h-[22px] min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
    'rounded-[11px] border border-solid px-2 align-middle text-[11.5px] font-semibold leading-[20px] tracking-normal',
    'border-[color:var(--category-pill-line)] bg-[var(--category-pill-bg)] text-[color:var(--category-pill-text)]',
    'dark:border-[color:var(--category-pill-dark-line)] dark:bg-[var(--category-pill-dark-bg)]',
    'dark:text-[color:var(--category-pill-dark-text)]',
    '[html[data-theme^=highContrast]_&]:border-current [html[data-theme^=highContrast]_&]:bg-transparent',
);

/**
 * One category value as a compact coloured pill; a blank value (or the legacy
 * "Unknown") as a dash in secondary text. The colour is stable per category
 * (`data-category-hue` names its palette hue), and a value too long for its
 * cell ends in an ellipsis with the full value in `title`. `className` and
 * `style` add to the pill's; other props go to its <span>.
 */
function CategoryPill({ value, className, style, ...other }) {
    const hue = getCategoryPillHue(value);
    if (!hue) {
        return (
            <span className={cx('text-content-secondary', className)} style={style} {...other}>
                —
            </span>
        );
    }
    const display = categoryDisplay(value);
    return (
        <span
            title={display}
            data-category-hue={hue.name}
            className={cx(PILL_CLASS_NAME, className)}
            style={{
                '--category-pill-bg': hue.light.background,
                '--category-pill-text': hue.light.color,
                '--category-pill-line': hue.light.borderColor,
                '--category-pill-dark-bg': hue.dark.background,
                '--category-pill-dark-text': hue.dark.color,
                '--category-pill-dark-line': hue.dark.borderColor,
                ...style,
            }}
            {...other}
        >
            {display}
        </span>
    );
}

CategoryPill.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    className: PropTypes.string,
    style: PropTypes.object,
};

export default React.memo(CategoryPill);
